<template>
  <div class="qa-record-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <h1 class="page-title">用户答疑记录</h1>
      <p class="page-subtitle">查看您与专家的问答记录</p>
    </div>

    <!-- 空数据状态 -->
    <div v-if="qaRecords.length === 0" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </div>
      <h3 class="empty-title">暂无答疑记录</h3>
      <p class="empty-desc">您还没有进行过专家答疑，快去提问吧</p>
    </div>
    
     
    <!-- 答疑列表 -->
    <div v-else class="qa-list">
      <el-card
        v-for="record in qaRecords"
        :key="record.id"
        class="qa-card"
        :class="{ 'has-answer': record.answer }"
        shadow="hover"
      >
        <!-- 卡片头部 -->
        <div class="qa-header">
          <div class="qa-meta">
            <span class="qa-time-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </span>
            <span class="qa-time">{{ record.createTime }}</span>
          </div>
          <el-tag 
            :type="record.isRead === 1 ? 'success' : 'warning'" 
            :effect="record.isRead === 1 ? 'dark' : 'light'"
            class="status-tag"
          >
            <span class="status-dot"></span>
            {{ record.isRead === 1 ? '已回复' : '待回复' }}
          </el-tag>
        </div>

        <!-- 卡片内容 -->
        <div class="qa-content">
          <!-- 问题区域 -->
          <div class="question-section">
            <div class="question-header">
              <span class="question-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M8.5 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zM15.5 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"></path>
                  <path d="M8 15h8"></path>
                  <path d="M8 19h8"></path>
                </svg>
              </span>
              <span class="question-label">问题</span>
            </div>
            <p class="question-text">{{ record.questions }}</p>
          </div>

          <!-- 回答区域 -->
          <div v-if="record.answer" class="answer-section">
            <div class="answer-header">
              <span class="answer-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 16 16 12 12 8"></polyline>
                  <polyline points="8 12 12 16 16 12"></polyline>
                </svg>
              </span>
              <span class="answer-label">专家回复</span>
            </div>
            <p class="answer-text">{{ record.answer }}</p>
            <div v-if="record.answertime" class="answer-footer">
              <span class="answer-time">回复时间：{{ record.answertime }}</span>
            </div>
          </div>

          <!-- 未回复状态 -->
          <div v-else class="pending-section">
            <el-tag type="info" effect="light" class="pending-tag">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="pending-icon">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 6v6l4 2"></path>
              </svg>
              专家正在处理中，请耐心等待
            </el-tag>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 分页组件 -->
    <div v-if="pagination.total > 0" class="pagination-wrapper">
      <el-pagination
        :current-page="pagination.pageNum"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :page-sizes="[3, 5, 10]"
        layout="total, sizes, prev, pager, next, jumper"
        background
        class="pagination"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick } from 'vue';
import { getExpertAnswerList } from '@/api/expertApi';

const userInfo = JSON.parse(localStorage.getItem('userInfo'));

const qaRecords = ref([]);
const pagination = reactive({
  pageNum: 1,
  pageSize: 3,
  total: 0,
  pages: 0
});

onMounted(() => {
  fetchQARecords();
});

const fetchQARecords = async () => {
  try {
    const response = await getExpertAnswerList(pagination.pageNum, pagination.pageSize);
    if (response.data.code === 200) {
      qaRecords.value = response.data.data.list || [];
      pagination.total = response.data.data.total || 0;
      pagination.pages = response.data.data.pages || 0;
    }
  } catch (error) {
    console.error('获取答疑记录失败:', error);
  }
};

const handleSizeChange = async (val) => {
  pagination.pageSize = val;
  pagination.pageNum = 1;
  // 等待数据加载完成
  await fetchQARecords();
  // 等待 DOM 更新完成
  await nextTick();
  // 延迟滚动确保效果
  setTimeout(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 150);
};

const handleCurrentChange = async (val) => {
  pagination.pageNum = val;
  // 等待数据加载完成
  await fetchQARecords();
  // 等待 DOM 更新完成
  await nextTick();
  // 延迟滚动确保效果
  setTimeout(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 150);
};
</script>

<style scoped>
.qa-record-page {
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  padding: 20px 15px;
}

/* 页面头部 */
.page-header {
  text-align: center;

  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 6px 0;
}

.page-subtitle {
  font-size: 13px;
  color: #666;
  margin: 0;
}

/* 空数据状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.empty-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 15px;
  color: #ccc;
}

.empty-icon svg {
  width: 100%;
  height: 100%;
}

.empty-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 6px 0;
}

.empty-desc {
  font-size: 13px;
  color: #999;
  margin: 0;
}

/* 答疑列表 */
.qa-list {
  display: grid;
  gap: 12px;
  max-width: 800px;
  margin: 0 auto 20px;
}

/* 卡片样式 */
.qa-card {
  background: #fff;
  border-radius: 10px;
  border: none;
  overflow: hidden;
  transition: all 0.2s ease;
}

.qa-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.qa-card.has-answer {
  border-left: 3px solid #409EFF;
}

/* 卡片头部 */
.qa-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #f5f5f5;
}

.qa-meta {
  display: flex;
  align-items: center;
  gap: 6px;
}

.qa-time-icon {
  width: 14px;
  height: 14px;
  color: #999;
}

.qa-time-icon svg {
  width: 100%;
  height: 100%;
}

.qa-time {
  font-size: 12px;
  color: #999;
}

.status-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
}

.status-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
}

/* 卡片内容 */
.qa-content {
  padding: 12px 14px;
}

/* 问题区域 */
.question-section {
  margin-bottom: 12px;
}

.question-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.question-icon {
  width: 14px;
  height: 14px;
  color: #409EFF;
}

.question-icon svg {
  width: 100%;
  height: 100%;
}

.question-label {
  font-size: 11px;
  font-weight: 500;
  color: #409EFF;
  padding: 1px 6px;
  background: rgba(64, 158, 255, 0.1);
  border-radius: 3px;
}

.question-text {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  margin: 0;
  padding: 8px 12px;
  background: #fafbfc;
  border-radius: 6px;
}

/* 回答区域 */
.answer-section {
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%);
  border-radius: 8px;
  padding: 10px 12px;
}

.answer-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.answer-icon {
  width: 14px;
  height: 14px;
  color: #67c23a;
}

.answer-icon svg {
  width: 100%;
  height: 100%;
}

.answer-label {
  font-size: 11px;
  font-weight: 500;
  color: #67c23a;
  padding: 1px 6px;
  background: rgba(103, 194, 58, 0.1);
  border-radius: 3px;
}

.answer-text {
  font-size: 13px;
  color: #333;
  line-height: 1.6;
  margin: 0 0 8px 0;
}

.answer-footer {
  padding-top: 8px;
  border-top: 1px dashed #d0d7de;
}

.answer-time {
  font-size: 11px;
  color: #999;
}

/* 未回复状态 */
.pending-section {
  text-align: center;
  padding: 12px;
}

.pending-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
}

.pending-icon {
  width: 12px;
  height: 12px;
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 15px 0;
}

.pagination {
  background: #fff;
  border-radius: 6px;
  padding: 6px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.04);
}

/* 响应式 */
@media (max-width: 768px) {
  .qa-record-page {
    padding: 15px 10px;
  }

  .page-title {
    font-size: 20px;
  }

  .qa-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .qa-content {
    padding: 10px;
  }

  .question-text {
    padding: 6px 10px;
  }
}
</style>